import * as React from 'react';
import capitalize from 'lodash/capitalize';
import {TypeImplementation, Expressions} from '@packages/systems/wfdl';
import {EHole} from '@packages/systems/wfdl-ast';

import {styledDiv} from '@packages/utilities/styled';
import {selectCMSCollections} from '../utils';
import {CollectionSettings} from '../components/CollectionSettings';
import {useSharedFlowContext, LogicLabel} from '@packages/systems/flows/shared';

import {updateExpressionAtAction} from '@packages/systems/ExpressionEditor-utils';
import {confirm} from '@packages/systems/dialog';
import {a, tyfun, error} from '@packages/utilities/wfdl-helpers';
import {TEffect} from '@packages/systems/plugins/Effect';
import {TJson} from '@packages/systems/plugins/Json';
import Grid from '@packages/systems/design-system/Grid';
import {Option, Select} from '@packages/systems/design-system/WebflowSelect';
import {selectCmsItemUpdatedTrigger} from '@packages/systems/flows/shared/selectors';
import {
  updateIfNeeded,
  updatePropertyIfNeeded,
} from '@packages/utilities/POJORecordUtils';
import {useDesignerSelector} from '@packages/systems/designer/flux';

/**
 * -----------------------------------------------------------
 * NOTE — this UI will potentially evolve to support subscribing
 * to more than one field. We are hard coding "Status" and
 * "Item published/unpublished" for Open beta on 9/13/2022.
 *
 * Eventually if we start to add support for more fields (requiring that
 * we get all fields), we'll need to create or reuse existing logic
 * that gets all the fields for a collection, which we can look to
 * ItemUpdaters.jsx for inspiration.
 * -----------------------------------------------------------
 */
const temp_fieldNames = ['status'];
const temp_statusValues = ['Item published', 'Item unpublished'];

const reducer = (
  state: {
    collectionName: null | string;
    fieldName: string;
    fieldValue: null | string;
  },
  action:
    | {
        type: 'COLLECTION_NAME_CHANGED';
        payload: string;
      }
    | {
        type: 'SELECT_FIELD_NAME_CHANGED';
        payload: string;
      }
    | {
        type: 'SELECT_FIELD_VALUE_CHANGED';
        payload: string;
      }
    | {
        type: 'RESET';
        payload: {
          collectionName: null | string;
          fieldValue: null | string;
        };
      }
) => {
  switch (action.type) {
    case 'COLLECTION_NAME_CHANGED': {
      return updatePropertyIfNeeded(state, 'collectionName', action.payload);
    }
    case 'SELECT_FIELD_NAME_CHANGED': {
      return updatePropertyIfNeeded(state, 'fieldName', action.payload);
    }
    case 'SELECT_FIELD_VALUE_CHANGED': {
      return updatePropertyIfNeeded(state, 'fieldValue', action.payload);
    }
    case 'RESET': {
      return updateIfNeeded(state, {
        collectionName: action.payload.collectionName,
        fieldValue: action.payload.fieldValue,
      });
    }
    default:
      return state;
  }
};

function useFieldSelectState(
  initialCollectionName: undefined | string,
  initialFieldName: undefined | string,
  // @ts-expect-error - TS7006 - Parameter 'initialFieldValue' implicitly has an 'any' type.
  initialFieldValue
) {
  const [state, dispatch] = React.useReducer(reducer, {
    collectionName: initialCollectionName || null,
    fieldName: initialFieldName || 'status',
    fieldValue: initialFieldValue || null,
  });

  const handlers = React.useRef({
    selectCollectionName(collectionName: string) {
      dispatch({
        type: 'COLLECTION_NAME_CHANGED',
        payload: collectionName,
      });
    },
    selectFieldName(fieldName: string) {
      dispatch({type: 'SELECT_FIELD_NAME_CHANGED', payload: fieldName});
    },
    selectFieldValue(fieldValue: string) {
      dispatch({type: 'SELECT_FIELD_VALUE_CHANGED', payload: fieldValue});
    },
    // @ts-expect-error - TS7006 - Parameter 'fieldValue' implicitly has an 'any' type.
    reset(collectionName: null | string, fieldValue) {
      dispatch({type: 'RESET', payload: {fieldValue, collectionName}});
    },
  }).current;

  return [state, handlers];
}

function useCmsUpdatedTriggerData() {
  const cmsFlowTrigger = useDesignerSelector(selectCmsItemUpdatedTrigger);
  const meta = cmsFlowTrigger?.meta;
  const field = meta?.subscribed?.fields?.[0];
  const collectionName = meta?.collectionName;

  return [field, collectionName];
}

const Divider = styledDiv(({theme: {gutters, colors}}) => ({
  height: 1,
  backgroundColor: colors.backgroundDeep,
  width: `calc(100% + ${gutters.x8 * 2 + gutters.x4}px)`,
  marginLeft: -gutters.x8 - gutters.x4 / 2,
}));

/**
 * Type for the CMS item updated trigger UI
 */
export const ItemUpdatedTrigger = TypeImplementation.Alias({
  params: ['error', 'a'],
  system: {bindUIVariant: 'custom'},
  type: tyfun([TJson], TEffect(error, a)),
  render: function ItemDeleters({dispatch}) {
    const {useCMSBlock} = useSharedFlowContext();
    const {hasDependencies} = useCMSBlock();
    const collections = useDesignerSelector(selectCMSCollections);
    const [field, initialName] = useCmsUpdatedTriggerData();
    const [fieldState, fieldHandlers] = useFieldSelectState(
      initialName,
      field?.name,
      field?.value
    );
    // @ts-expect-error - TS2339 - Property 'collectionName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
    const name = fieldState.collectionName;

    React.useEffect(
      function resetOnCollectionNameChange() {
        // @ts-expect-error - TS2339 - Property 'reset' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'. | TS2551 - Property 'value' does not exist on type 'string | { name: string; value: any; } | ((nextCollectionName: string, subscribed: any) => void)'. Did you mean 'valueOf'?
        fieldHandlers.reset(initialName || null, field?.value || null);
      },
      [field, fieldHandlers, initialName]
    );

    const onCollectionChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'collectionName' implicitly has an 'any' type.
      async (collectionName) => {
        const coll = collections.find((c) => c.pluralName === collectionName);
        if (!coll) return;

        if (hasDependencies) {
          confirm({
            title: 'Change Collection?',
            content:
              "You're currently using data from the current collection in other parts of the flow. Changing a collection might break the flow.",
            submit: {label: 'Change Collection', intent: 'primary'},
            onSubmit() {
              dispatch(updateExpressionAtAction(EHole(), []));
              // @ts-expect-error - TS2339 - Property 'selectCollectionName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
              fieldHandlers.selectCollectionName(collectionName);
            },
          });
        } else {
          dispatch(updateExpressionAtAction(EHole(), []));
          // @ts-expect-error - TS2339 - Property 'selectCollectionName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
          fieldHandlers.selectCollectionName(collectionName);
        }
      },
      [dispatch, hasDependencies, collections, fieldHandlers]
    );

    const selectFieldValue = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'value' implicitly has an 'any' type. | TS7006 - Parameter 'meta' implicitly has an 'any' type.
      (value, meta) => {
        if (meta.changeType !== 'changeEnd') return;
        // @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
        if (!value || fieldState.fieldName === value) return;

        if (name) {
          dispatch(
            updateExpressionAtAction(
              Expressions.qualifiedVariable(
                '__SitePlugin',
                `CMS Trigger: ${name} updated`
              ),
              []
            )
          );
          // @ts-expect-error - TS2339 - Property 'selectFieldValue' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
          fieldHandlers.selectFieldValue(value);
        }
      },
      [
        dispatch,
        name,
        // @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
        fieldState.fieldName,
        fieldHandlers,
      ]
    );

    return (
      <Grid gridGap={2}>
        <div>
          <CollectionSettings
            onChange={onCollectionChange}
            // @ts-expect-error - TS2339 - Property 'collectionName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
            value={fieldState.collectionName}
            collections={collections}
            blockType="Updated"
          />
        </div>

        {name != null && (
          <>
            <Divider />

            <div>
              <LogicLabel label="Select field" />

              <Select
                id="subscribe-to-field-name-select"
                // @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
                value={fieldState.fieldName}
                // @ts-expect-error - TS2339 - Property 'selectFieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
                onChange={fieldHandlers.selectFieldName}
                placeholder={<span>Select a field...</span>}
              >
                {temp_fieldNames.map((fieldName, i) => (
                  <Option
                    key={`${name}-subscribe-${fieldName}-${i}`}
                    value={fieldName}
                    label={capitalize(fieldName)}
                  />
                ))}
              </Select>
            </div>

            {/* @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'. */}
            {fieldState.fieldName != null && (
              <div>
                {/* @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'. */}
                <LogicLabel label={`Select ${fieldState.fieldName}`} />

                <Select
                  id="subscribe-to-field-value-select"
                  // @ts-expect-error - TS2339 - Property 'fieldValue' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
                  value={fieldState.fieldValue}
                  onChange={selectFieldValue}
                  placeholder={
                    // @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
                    <span>Select a value for {fieldState.fieldName}...</span>
                  }
                >
                  {temp_statusValues.map((statusValue, i) => (
                    <Option
                      // @ts-expect-error - TS2339 - Property 'fieldName' does not exist on type '{ collectionName: string | null; fieldName: string; fieldValue: string | null; } | { selectCollectionName(collectionName: string): void; selectFieldName(fieldName: string): void; selectFieldValue(fieldValue: string): void; reset(collectionName: string | null, fieldValue: any): void; }'.
                      key={`${name}-${fieldState.fieldName}-${statusValue}-${i}`}
                      value={statusValue}
                      label={capitalize(statusValue)}
                    />
                  ))}
                </Select>
              </div>
            )}
          </>
        )}
      </Grid>
    );
  },
});
